import { useEffect, useMemo, useRef, useState } from "react";
import { FirstVisitDocsChecklist } from "../components/founder/FirstVisitDocsChecklist";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { trackPurchase, trackScoreComplete } from "../lib/ga";
import AppShell from "../components/AppShell";
import { Upload, CheckCircle2, FileText, Loader2, ArrowRight, Sparkles, X, Users, Trash2, AlertTriangle } from "lucide-react";
import { sdgLabel } from "../lib/sdg";
import { ReplaceOldVersionModal, SINGLE_VERSION_DOC_TYPES } from "../components/founder/UpdateDocsRerunBanner";
import ReadinessReportCard, { RerunModal, BuyCreditsModal } from "../components/founder/ReadinessReportCard";
import { LaunchPrepButton } from "../components/LaunchPrepButton";
import { useAnalysisRunner, AnalysisProgressModal, AnalysisSubmittedCard, phaseLabel } from "../lib/analysisRunner";
import { aiAutofillErrorMessage } from "../lib/aiAutofillError";
import { showOversizedFileToast } from "../lib/uploadToast";
import { CapitalDocChecklist } from "../components/founder/CapitalDocChecklist";
import { CAP_DOC_CATALOG, buildChecklist } from "../lib/capitalDocChecklist";

// Quiz "which documents do you have" ids → upload doc types.
const QUIZ_TO_DOC_TYPE = {
  bank_statements: "bank_statements",
  pitch_deck: "pitch_deck",
  mgmt_accounts: "mgmt_accounts",
  financial_model: "financial_model",
  audited_financials: "audited_accounts",
  certificate: "legal",
};
import { WizardStepper, WizardNav, ReportDownloadStep, WIZARD_STEPS } from "../components/founder/OnboardingWizard";
import { setWaContext } from "../lib/waContext";
import { BUSINESS_SNAPSHOT_QUESTIONS } from "../lib/businessSnapshotQA";
import { QuizSnapshotCard } from "../components/founder/QuizSnapshotCard";
import { ReportValueProp } from "../components/founder/ReportValueProp";
import { toast } from "sonner";
import { RaisePurposePicker } from "../components/founder/RaisePurposePicker";
import { BusinessSnapshotQA } from "../components/founder/BusinessSnapshotQA";
import { GetHelpLink } from "../components/founder/SupportTicketModal";
import { pdfLooksEncrypted } from "../lib/pdfEncryption";
import { EncryptedPdfDialog } from "../components/founder/EncryptedPdfDialog";
import { UploadFailedBadge } from "../components/founder/UploadFailedBadge";

const SECTORS = ["Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Hospitality", "Other"];
const COUNTRIES = ["Nigeria", "Kenya", "Ghana", "South Africa", "Egypt", "Rwanda", "Senegal", "Ethiopia", "Tanzania", "Uganda"];
const STAGES = ["Idea", "MVP", "Revenue", "Growth"];
const SDGS = ["SDG 2", "SDG 3", "SDG 4", "SDG 5", "SDG 7", "SDG 8", "SDG 9", "SDG 13"];
const BUSINESS_ROLES = ["Founder", "Co-founder", "CEO", "CFO", "COO", "CTO", "Head of Product", "Board member", "Advisor", "Other"];

const NAV = [
  {to:"/onboarding",label:"Onboarding"},
  {to:"/founder/dashboard",label:"Dashboard"},
  {to:"/founder/team",label:"Team"},
  {to:"/founder/support",label:"Support"},
];

// CRS v2.1 document pack — kept in sync with /app/backend/routers/founder.py ALLOWED_DOC_TYPES.
const CORE_DOC_TYPES = [
  { key: "pitch_deck",      label: "Pitch deck",
    hint: "PDF / PPT — your standard funder deck",
    why:  "This is the story of your business. We use it to see who's on the team, what you sell, who you sell to, and how big the opportunity is." },
  { key: "financial_model", label: "Financial model",
    hint: "XLS / CSV / PDF — projections, P&L, assumptions",
    why:  "Shows that you know your numbers. We pull your revenue, costs and growth plan from here — funders will ask for this on day one." },
  { key: "mgmt_accounts",   label: "Management accounts",
    hint: "Last 3 years (or what you have). Multiple files OK",
    why:  "Proves the revenue you mentioned is real. The cleaner and more recent these are, the easier it is for a lender or investor to trust you." },
  { key: "legal",           label: "Legal docs",
    hint: "Company registration, articles, board resolutions, etc.",
    why:  "Confirms your company actually exists on paper. No funder can give you money if you can't show you're a properly registered business." },
];

const UNLOCK_DOC_TYPES = [
  { key: "bank_statements", label: "Bank statements (6+ months)",
    hint: "Last 6+ months of business bank statements (PDF / CSV)",
    why:  "Lets us prove your monthly revenue to lenders. Without this, you can't qualify for revenue-based finance — the fastest non-dilutive money out there." },
  { key: "audited_accounts", label: "Audited accounts",
    hint: "Most recent audited annual accounts (PDF)",
    why:  "Required by banks and big development funders before they'll talk to you. Audited = independently checked, so they can trust your numbers." },
  { key: "tax_compliance", label: "Tax compliance certificate",
    hint: "Current tax compliance / TCC / PIN / VAT certificate",
    why:  "Shows you've paid your taxes. Banks and most large funders won't release money to a business that's behind with the tax office." },
  { key: "cap_table", label: "Cap table",
    hint: "Spreadsheet showing all shareholders + instrument types",
    why:  "A simple list of who owns what slice of the company. Investors need to see this before they consider buying in." },
  { key: "purchase_orders", label: "POs / invoices / supply agreements",
    hint: "Signed purchase orders, invoices, or supplier agreements",
    why:  "Proof that customers are actually buying. Unlocks short-term financing options that pay your suppliers while you wait to get paid." },
  { key: "asset_schedules", label: "Asset schedules / equipment quotes",
    hint: "Equipment lists, asset registers, or quotes for CapEx",
    why:  "Lets you borrow against equipment you need (or already own). The equipment itself becomes the security — no shares given away." },
  { key: "impact_reports", label: "Impact / SDG reports",
    hint: "Impact KPIs, SDG alignment, or theory-of-change docs",
    why:  "Opens the door to impact and development funders. They have cheaper money but specifically need proof of the good your business does." },
];

// Filename → doc_type detection. Patterns are intentionally simple to keep
// the matcher predictable; the first matching pattern wins, and pattern order
// puts more-specific keywords (audited / tax compliance) before general ones
// (accounts / statement) to avoid mis-classification.
//
// IMPORTANT: do NOT use `\b` word-boundary anchors here. JS regex `\b` treats
// underscores as word characters, so `/\baudit\b/i` does NOT match
// `audited_accounts.pdf` (no boundary between `d` and `_`). For filename
// matching we rely on (a) specific multi-char keywords and (b) the
// `[\s_.\-]` connector class where word-separation is required.
const FILENAME_DETECTORS = [
  { key: "bank_statements",   pattern: /(bank[\s_.\-]*statement|bank[\s_.\-]*stmt)/i },
  { key: "audited_accounts",  pattern: /audit(ed)?/i },
  { key: "tax_compliance",    pattern: /(tax[\s_.\-]*(compliance|certificate|cert|clearance)|(^|[\s_.\-])tcc([\s_.\-]|$)|(^|[\s_.\-])vat([\s_.\-]|$)|pin[\s_.\-]*cert)/i },
  { key: "cap_table",         pattern: /(cap[\s_.\-]*table|shareholder|share[\s_.\-]*register)/i },
  { key: "purchase_orders",   pattern: /(purchase[\s_.\-]*order|(^|[\s_.\-])po[s]?[\s_.\-]|invoice|supply[\s_.\-]*agreement)/i },
  { key: "asset_schedules",   pattern: /(asset[\s_.\-]*(schedule|register|list)|equipment[\s_.\-]*(quote|schedule|list))/i },
  { key: "impact_reports",    pattern: /(impact[\s_.\-]*report|(^|[\s_.\-])sdg[s]?([\s_.\-]|$)|(^|[\s_.\-])esg([\s_.\-]|$)|theory[\s_.\-]*of[\s_.\-]*change)/i },
  { key: "pitch_deck",        pattern: /(pitch|deck|investor[\s_.\-]*present)/i },
  { key: "financial_model",   pattern: /(financial[\s_.\-]*model|fin[\s_.\-]*model|forecast|projection|(^|[\s_.\-])model([\s_.\-]|$))/i },
  { key: "mgmt_accounts",     pattern: /(management[\s_.\-]*account|mgmt[\s_.\-]*account|p[\s_.\-]*&[\s_.\-]*l|profit[\s_.\-]*and[\s_.\-]*loss|income[\s_.\-]*statement)/i },
  { key: "legal",             pattern: /(certificate[\s_.\-]*of[\s_.\-]*incorporation|articles[\s_.\-]*of[\s_.\-]*assoc|memorandum|board[\s_.\-]*resolution|business[\s_.\-]*permit)/i },
];

function detectDocType(filename) {
  if (!filename) return null;
  for (const d of FILENAME_DETECTORS) {
    if (d.pattern.test(filename)) return d.key;
  }
  return null;
}

const ALL_DOC_TYPE_LABELS = [...CORE_DOC_TYPES, ...UNLOCK_DOC_TYPES]
  .reduce((acc, c) => { acc[c.key] = c.label; return acc; },
          { ...CAP_DOC_CATALOG, company_profile: "Company profile" });

export default function Onboarding() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  // Flow v2 (Oct 2026): uploads + one free documentation score come BEFORE the
  // paywall; the report purchase gates the full analysis. Legacy founders keep
  // the Step-1 paywall. `freeScoreUsed` = they already took the free score.
  const flowV2 = !!user?.flow_v2;
  const freeScoreUsed = !!user?.free_score_used;
  const [freeScoreBusy, setFreeScoreBusy] = useState(false);
  // First login after sign-up (legacy flow): show the one-time freemium snapshot first.
  useEffect(() => {
    if (user?.welcome_snapshot_due && !flowV2) nav("/welcome-snapshot", { replace: true });
  }, [user?.welcome_snapshot_due, flowV2, nav]);
  const [step] = useState(() => {
    // Step 1 (snapshot form) and Step 3 (verification form) were removed
    // in Feb 2026 — the AI auto-fill modal now extracts those fields from
    // the founder's pitch deck instead. Step 2 (Document upload + analysis)
    // is the only manual step now.
    const s = parseInt(params.get("step") || "2", 10);
    return s === 2 ? 2 : 2;
  });
  const [businessRole, setBusinessRole] = useState("");
  const [roleBusy, setRoleBusy] = useState(false);
  // Step 1 snapshot inputs — persisted across step navigation AND full page
  // refreshes via `clinq_onboarding_snap_v1`. Same lazy-init pattern as the
  // verify state below so saved values are loaded synchronously on first
  // mount and never clobbered by the save-effect.
  const _SNAP_DEFAULT = {
    company_name: "", website: "", sector: "Fintech", country: "Nigeria", stage: "Revenue",
    ticket_min: 250000, ticket_max: 1000000, value_prop: "", revenue_annual: 0, employees: 1, sdgs: [],
    target_funder_type: "seed_vc",
    collateral_availability: "none",
    founder_dilution_appetite: "flexible",
    monthly_recurring_revenue: 0,
    revenue_model_type: "",
    use_of_funds: "",
  };
  const [snap, setSnap] = useState(() => {
    try {
      const cached = localStorage.getItem("clinq_onboarding_snap_v1");
      if (cached) {
        const obj = JSON.parse(cached);
        if (obj && typeof obj === "object") return { ..._SNAP_DEFAULT, ...obj };
      }
    } catch (_) { /* ignore */ }
    return _SNAP_DEFAULT;
  });
  useEffect(() => {
    try {
      localStorage.setItem("clinq_onboarding_snap_v1", JSON.stringify(snap));
    } catch (_) { /* localStorage quota / privacy mode — ignore */ }
  }, [snap]);
  // Project ventures use the PRS intake, not the business onboarding.
  useEffect(() => {
    api.get("/companies/me").then((r) => {
      if ((r.data?.kind || "business") === "project") {
        window.location.replace("/project/intake");
      }
    }).catch(() => {});
  }, []);
  const [docs, setDocs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  // Revenue stage drives the upload-step doc-tile filter. Pre-revenue
  // founders haven't traded yet, so historical / audit docs don't apply
  // — we hide Management accounts (core) and Audited accounts (unlocks).
  // Mirrors the same filter on the public Get-Started Quiz.
  const [revenueStage, setRevenueStage] = useState("");
  // Capital purposes from the Get Started quiz — drive the personalised
  // Required/Optional document checklist below the core upload tiles.
  const [capitalPurposes, setCapitalPurposes] = useState([]);
  // "I don't have these yet" flags — {mgmt_accounts: iso} from company.doc_deferrals.
  const [deferrals, setDeferrals] = useState({});

  const toggleDeferral = async (doc, defer) => {
    try {
      await api.post("/companies/me/doc-deferral", { doc, action: defer ? "defer" : "undo" });
      setDeferrals(d => {
        const next = { ...d };
        if (defer) next[doc] = new Date().toISOString(); else delete next[doc];
        return next;
      });
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("[Onboarding] deferral failed", e);
    }
  };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Per-doc-type uploading state: { [doc_type]: { filename, progress, vision } }
  // Lets the UploadCard show a live spinner + progress % + filename while bytes
  // are still in flight so the upload never appears frozen.
  const [uploading, setUploading] = useState({});
  // Persistent upload-error banner (not just a toast) — {title, detail, kind}.
  const [uploadError, setUploadError] = useState(null);
  const [encModal, setEncModal] = useState(null); // {file, docType} — encrypted bank statement
  const [replacePrompt, setReplacePrompt] = useState(null); // {fresh, olds} — second version of a single-version doc type
  const [replacing, setReplacing] = useState(false);
  const offerReplace = (fresh) => {
    if (!fresh?.doc_id || !SINGLE_VERSION_DOC_TYPES.has(fresh.doc_type)) return;
    const olds = docs.filter((d) => d.doc_type === fresh.doc_type && d.doc_id !== fresh.doc_id);
    if (olds.length) setReplacePrompt({ fresh, olds });
  };
  const replaceOld = async () => {
    if (!replacePrompt) return;
    setReplacing(true);
    const ids = replacePrompt.olds.map((d) => d.doc_id);
    try {
      await Promise.all(ids.map((id) => api.delete(`/documents/${id}`)));
      setDocs((ds) => ds.filter((d) => !ids.includes(d.doc_id)));
      toast.success("Replaced the previous version", { description: `${replacePrompt.fresh.filename} is now the only version in your pack.` });
      setReplacePrompt(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove the old version"); }
    finally { setReplacing(false); }
  };
  const [encBusy, setEncBusy] = useState(false);
  const [encErr, setEncErr] = useState("");
  // Current user's email — pulled from /auth/me on mount. Powers the
  // "Trouble uploading?" mailto fallback so the message prefills the founder's
  // context (email + company name) for the Clinq ops team.
  const [userEmail, setUserEmail] = useState("");
  // Step 3 verification inputs — persisted across step navigation AND full
  // page refreshes via `clinq_onboarding_verify_v1`. The lazy `useState`
  // initialiser is critical: it reads localStorage SYNCHRONOUSLY on first
  // mount so the save-effect can't fire with empty defaults and clobber
  // previously-saved values before the load could populate them.
  const [verify, setVerify] = useState(() => {
    const base = { legal_name: "", registration_number: "", director_name: "", nda_accepted: true };
    try {
      const cached = localStorage.getItem("clinq_onboarding_verify_v1");
      if (cached) {
        const obj = JSON.parse(cached);
        if (obj && typeof obj === "object") return { ...base, ...obj };
      }
    } catch (_) { /* ignore parse / quota errors */ }
    return base;
  });
  useEffect(() => {
    try {
      localStorage.setItem("clinq_onboarding_verify_v1", JSON.stringify({
        legal_name: verify.legal_name,
        registration_number: verify.registration_number,
        director_name: verify.director_name,
        nda_accepted: !!verify.nda_accepted,
      }));
    } catch (_) { /* ignore quota errors */ }
  }, [verify]);
  const [unlocksOpen, setUnlocksOpen] = useState(false);
  // Pre-fill highlights from the public Get-Started Quiz. The landing-page
  // quiz writes `{stage, docs: [pitch_deck|mgmt_accounts|financial_model|
  // audited_financials|certificate]}` to localStorage under
  // `clinq_get_started_v1`. We map those to onboarding doc_type keys and:
  //   • show a one-off banner reminding the founder which docs they said
  //     they have,
  //   • highlight the matching upload cards (amber ring + badge),
  //   • auto-open the "Unlock more capital types" panel iff a highlighted
  //     doc lives in the unlock pack.
  const [quizDocKeys, setQuizDocKeys] = useState(() => new Set());
  const [quizBannerDismissed, setQuizBannerDismissed] = useState(() => {
    try { return localStorage.getItem("clinq_quiz_banner_dismissed_v1") === "1"; }
    catch (_) { return false; }
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem("clinq_get_started_v1");
      if (!raw) return;
      const obj = JSON.parse(raw);
      const quizDocs = Array.isArray(obj?.docs) ? obj.docs : [];
      const mapped = new Set(
        quizDocs.map((q) => QUIZ_TO_DOC_TYPE[q]).filter(Boolean)
      );
      if (mapped.size === 0) return;
      setQuizDocKeys(mapped);
      // Auto-open the unlock panel if any highlighted doc lives there.
      const inUnlocks = UNLOCK_DOC_TYPES.some((c) => mapped.has(c.key));
      if (inUnlocks) setUnlocksOpen(true);
    } catch (_) { /* ignore parse errors */ }
  }, []);
  const dismissQuizBanner = () => {
    setQuizBannerDismissed(true);
    try { localStorage.setItem("clinq_quiz_banner_dismissed_v1", "1"); }
    catch (_) { /* ignore */ }
  };
  // Apply the pre-revenue doc-type filter to the upload cards. Hidden
  // tiles: `mgmt_accounts` (core) + `audited_accounts` (unlocks). Already-
  // uploaded files for hidden types stay on the company record — we just
  // don't render the empty tile or include them in the "X/Y uploaded" count.
  const _PRE_REV_HIDDEN = new Set(["mgmt_accounts", "audited_accounts"]);
  const visibleCoreDocTypes = revenueStage === "pre_revenue"
    ? CORE_DOC_TYPES.filter((c) => !_PRE_REV_HIDDEN.has(c.key))
    : CORE_DOC_TYPES;
  const visibleUnlockDocTypes = revenueStage === "pre_revenue"
    ? UNLOCK_DOC_TYPES.filter((c) => !_PRE_REV_HIDDEN.has(c.key))
    : UNLOCK_DOC_TYPES;
  // When the personalised capital checklist leads the page, the standard
  // tiles it doesn't cover (e.g. pitch deck, legal docs) move into the
  // optional "more documents" panel so nothing becomes unreachable.
  const capChecklistIds = new Set(
    capitalPurposes.length > 0 ? buildChecklist(capitalPurposes).map((i) => i.id) : []);
  const extraOptionalDocTypes = capitalPurposes.length > 0
    ? [...visibleCoreDocTypes, ...visibleUnlockDocTypes].filter((c) => !capChecklistIds.has(c.key))
    : visibleUnlockDocTypes;
  const unlockedCount = extraOptionalDocTypes.filter(
    (c) => docs.some((d) => d.doc_type === c.key)
  ).length;
  // Filename-mismatch confirmation dialog state.
  // null when no mismatch; otherwise { file, intended, detected }.
  const [mismatch, setMismatch] = useState(null);
  // doc_ids that were auto-classified by the filename detector — drives a
  // small "auto-classified" badge next to those filenames in the tile.
  const [autoClassifiedIds, setAutoClassifiedIds] = useState(new Set());

  // "Still reading your documents…" retry state. When the founder clicks
  // Run AI Analysis while vision extraction on an image-based PPTX deck is
  // still cooking, the backend returns 409 with a `still reading your
  // uploaded documents` detail. Instead of surfacing that as a scary red
  // error banner, we keep the progress modal visible on the "Reading your
  // documents" phase and quietly retry every 8s until the deck's text is
  // ready. `waitingForDocs` is the guard flag; `retryTimerRef` holds the
  // scheduled retry so we can cancel it on unmount / success.
  const [waitingForDocs, setWaitingForDocs] = useState(false);
  const retryTimerRef = useRef(null);
  useEffect(() => () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, []);

  // Shared analysis-runner hook — handles POST → poll-status → phase progress.
  const analyzer = useAnalysisRunner({
    startEndpoint: "/ai/analyze",
    onDone: (payload) => {
      setWaitingForDocs(false);
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      if (payload?.analysis) setAnalysis(payload.analysis);
    },
    onError: (msg) => {
      const stillReading = typeof msg === "string" &&
        /still reading your uploaded documents/i.test(msg);
      if (stillReading) {
        // Keep the progress popup visible and retry in the background —
        // vision extraction on image-based PPTX decks takes 20-60s.
        setWaitingForDocs(true);
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          analyzer.run();
        }, 8000);
        return;
      }
      setWaitingForDocs(false);
      setErr(msg);
    },
  });

  // Re-run flow — consolidated on the onboarding page so the founder never
  // has to bounce to the Readiness Report just to spend a credit. `report`
  // holds the checklist + credit balance that <RerunModal> needs; loaded
  // on-demand when the founder clicks "Re-run AI Analysis" or lands with
  // `?rerun=1` (deep-links from the Report page / stale-score nudges).
  const [rerunOpen, setRerunOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [rerunLoading, setRerunLoading] = useState(false);

  const openRerun = async ({ silent = false } = {}) => {
    setRerunLoading(true);
    try {
      const { data } = await api.get("/founder/readiness/report");
      setReport(data);
      const bal = Number(data?.credits_balance || 0);
      if (bal <= 0) setBuyOpen(true);
      else setRerunOpen(true);
    } catch (e) {
      // Auto-open from a stale `?rerun=1` bookmark on a fresh account (no
      // analysis yet) shouldn't show the red validation banner — that reads
      // like something went wrong. Toast a soft nudge instead.
      if (silent) {
        toast.info("Analyse my Documents first — then you can re-run to refresh your Capital Readiness Score.", { duration: 4000 });
      } else {
        setErr(e?.response?.data?.detail || "Couldn't load your report — try again.");
      }
    } finally {
      setRerunLoading(false);
    }
  };

  // Deep-link from the Readiness Report page (or an older bookmark):
  // `/onboarding?rerun=1` auto-opens the RerunModal once we're on step 2.
  useEffect(() => {
    if (params.get("rerun") !== "1") return;
    // Strip the param first so refresh / step change doesn't re-trigger.
    const next = new URLSearchParams(params);
    next.delete("rerun");
    setParams(next, { replace: true });
    toast.info("Starting your re-run…", { duration: 2000 });
    openRerun({ silent: true });
  }, []);


  // Post-rerun-redirect (from DetailedReport.jsx::runner.onDone) lands here
  // with `#score`. Scroll the ReadinessReportCard into view once it mounts
  // so the founder sees their fresh score front-and-centre. We wait for the
  // analysis to load AND the readiness card to render (it only renders when
  // `analysis` is truthy on line ~780), then scroll the target-banner element
  // into view. Cleared after first scroll so further hash navigations work.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#score") return;
    if (step !== 2 || !analysis) return;
    const t = setTimeout(() => {
      const el = document.querySelector("[data-testid='readiness-target-banner']");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Strip the hash without adding a history entry.
        window.history.replaceState(null, "", "/onboarding?step=2");
      }
    }, 600);
    return () => clearTimeout(t);
  }, [step, analysis]);

  // Deep-link from the Dashboard "Complete your dashboard" checklist:
  // `/onboarding?focus=role|revenue|urls|uploads` scrolls the matching
  // question into view so the founder doesn't have to hunt for it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const focus = params.get("focus");
    if (!focus) return;
    const targets = {
      role:     "[data-testid='business-role-picker']",
      revenue:  "[data-testid='revenue-stage-card']",
      urls:     "[data-testid='onb-website']",
      uploads:  "[data-testid='step-deep-dive']",
    };
    const sel = targets[focus];
    if (!sel) return;
    const t = setTimeout(() => {
      const el = document.querySelector(sel);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Pulse highlight so the eye finds it.
        el.classList.add("ring-2", "ring-[#0A8B85]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#0A8B85]", "ring-offset-2"), 2000);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [params]);


  // AI snapshot autofill
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiFile, setAiFile] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [aiSugg, setAiSugg] = useState(null);
  // Helper: surface an actionable "your deck is too big" toast that includes
  // a one-click link to iLovePDF compress (most founders don't know this
  // exists). Used by both the AI-Snapshot flow and the per-doc-slot uploader.
  const showOversizedDeckToast = (file, capMb) =>
    showOversizedFileToast(file, capMb);

  const runAiSnap = async () => {
    // Client-side size check — fail fast with a visible toast instead of letting
    // the network hand silently (some proxies, notably Cloudflare on prod,
    // truncate or reject oversized multipart POSTs without a clean error).
    if (aiFile && aiFile.size > 40 * 1024 * 1024) {
      const msg = "Your PDF is over the 40 MB limit. Try a slimmer export, or paste a description instead.";
      setAiErr(msg);
      showOversizedDeckToast(aiFile, 40);
      return;
    }
    setAiBusy(true); setAiErr(""); setAiSugg(null);
    // Persistent toast so the user knows the upload is in flight — the spinner
    // on the button alone has been missed by some founders on slow connections.
    const tId = toast.loading(aiFile ? `Reading ${aiFile.name}…` : "Extracting snapshot…");
    try {
      const fd = new FormData();
      if (aiText.trim()) fd.append("text", aiText.trim());
      if (aiFile) fd.append("file", aiFile);
      // IMPORTANT: do NOT set Content-Type manually here. axios + browser
      // auto-generate `multipart/form-data; boundary=...` from the FormData.
      // Setting it manually strips the boundary and some proxies (Cloudflare
      // on prod) silently drop the body — which presents as "nothing happens"
      // on the client (no spinner ever resolves).
      // 120s client timeout: must exceed the backend's ~90s worst-case budget
      // PLUS the multipart upload time (founders on slow links uploading a
      // multi-page deck), or axios aborts a request that's still working and
      // shows the misleading "AI took too long" message.
      const { data } = await api.post("/companies/me/snapshot/ai-suggest", fd,
        { timeout: 120000 });
      setAiSugg(data);
      toast.success("Snapshot extracted — review and apply.", { id: tId });
    } catch (e) {
      const msg = aiAutofillErrorMessage(e, { kind: "pitch" });
      setAiErr(msg);
      toast.error(msg, { id: tId });
    } finally { setAiBusy(false); }
  };
  const applyAiSnap = async () => {
    if (!aiSugg) return;
    // Step 1 (the manual snapshot form) was removed in Feb 2026 — applying
    // the AI suggestion now persists straight to the backend instead of
    // populating a form the founder still has to submit.
    const merged = { ...snap,
      company_name: aiSugg.company_name || snap.company_name,
      sector: aiSugg.sector || snap.sector,
      country: aiSugg.country || snap.country,
      stage: aiSugg.stage || snap.stage,
      ticket_min: aiSugg.ticket_min ?? snap.ticket_min,
      ticket_max: aiSugg.ticket_max ?? snap.ticket_max,
      revenue_annual: aiSugg.revenue_annual ?? snap.revenue_annual,
      employees: aiSugg.employees ?? snap.employees,
      value_prop: aiSugg.value_prop || snap.value_prop,
      sdgs: aiSugg.sdgs || snap.sdgs,
      // New fields (Feb 2026)
      legal_name: aiSugg.legal_name || null,
      registration_number: aiSugg.registration_number || null,
      director_name: aiSugg.director_name || null,
      revenue_fy2025: aiSugg.revenue_fy2025 ?? null,
      revenue_fy2026: aiSugg.revenue_fy2026 ?? null,
      ebitda_fy2025: aiSugg.ebitda_fy2025 ?? null,
      ebitda_fy2026: aiSugg.ebitda_fy2026 ?? null,
      capital_mix: aiSugg.capital_mix || null,
      // AI confidence ribbon (Feb 2026): surfaces on the report so founders
      // know which auto-filled fields to manually review before sharing.
      extraction_confidence: aiSugg.confidence ?? null,
      low_confidence_fields: aiSugg.low_confidence_fields || [],
    };
    setSnap(merged);
    setBusy(true); setErr("");
    try {
      await api.post("/companies/me/snapshot", merged);
      // If the AI also extracted incorporation legal data, persist it via the
      // verify endpoint so the data room can render legal fields. NDA defaults
      // to false here — it's now requested via a per-funder modal at access
      // approval time, not collected up-front.
      if (merged.legal_name && merged.registration_number && merged.director_name) {
        try {
          await api.post("/companies/me/verify", {
            legal_name: merged.legal_name,
            registration_number: merged.registration_number,
            director_name: merged.director_name,
            nda_accepted: false,
          });
        } catch (_) { /* legal autofill best-effort — don't block onboarding */ }
      }
      toast.success("Snapshot saved — now upload your documents.");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
    setAiOpen(false); setAiSugg(null); setAiText(""); setAiFile(null);
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  // Step-1 paywall (Sept 2026): nothing past Step 1 until the report is unlocked.
  const [reportUnlocked, setReportUnlocked] = useState(false);
  const [unlockTier, setUnlockTier] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [justPaid, setJustPaid] = useState(false);
  useEffect(() => { (async () => {
    try {
      // Returning from Paystack: confirm the payment before deciding the step.
      const params = new URLSearchParams(window.location.search);
      const sid = params.get("reference") || params.get("trxref");
      let paidNow = false;
      if (sid) {
        try {
          const { data: pollData } = await api.post(`/founder/credits/poll/${sid}`);
          paidNow = pollData?.payment_status === "paid";
          if (paidNow) trackPurchase(pollData, { report: "Capital Readiness Report" });
        } catch (_e) { /* fall through to balance check */ }
        window.history.replaceState({}, "", window.location.pathname);
      }
      let unlocked = false;
      try {
        let { data: bal } = await api.get("/founder/credits/balance");
        if (!bal?.report_unlocked && sid) {
          try {
            const { data: rec } = await api.post("/founder/credits/reconcile");
            if (rec?.unlocked) { bal = { ...bal, report_unlocked: true }; paidNow = true; }
          } catch (_e) { /* ignore */ }
        }
        unlocked = !!bal?.report_unlocked;
        setUnlockTier(bal?.tier || null);
      } catch (_e) { /* treated as locked */ }
      setReportUnlocked(unlocked);
      if (paidNow) {
        setJustPaid(true);
        try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ }
      }
      const { data } = await api.get("/companies/me");
      if (data?.company_name) setSnap(s => ({ ...s, ...data }));
      if (data?.ai_analysis) setAnalysis(data.ai_analysis);
      if (data?.revenue_stage) setRevenueStage(data.revenue_stage);
      if (Array.isArray(data?.capital_purposes)) setCapitalPurposes(data.capital_purposes);
      if (data?.doc_deferrals) setDeferrals(data.doc_deferrals);
      if (data?.get_started_quiz) setQuizSnap(data.get_started_quiz);
      const list = await api.get("/documents/me");
      setDocs(list.data || []);
      const me = await api.get("/auth/me");
      if (me.data?.business_role) setBusinessRole(me.data.business_role);
      if (me.data?.email) setUserEmail(me.data.email);
      // Guided wizard: founders who haven't downloaded their report yet get
      // the step-by-step flow, resumed at the right step from saved data
      // (works across sign-outs and devices — no local state involved).
      if (!data?.report_downloaded_at) {
        const docsArr = list.data || [];
        const purposes = Array.isArray(data?.capital_purposes) ? data.capital_purposes : [];
        const reasons = Array.isArray(data?.raise_reasons) ? data.raise_reasons : [];
        const s1Basics = !!(data?.company_name && data?.sector && data?.country);
        const qaAns = (data?.business_snapshot_qa || {}).answers || {};
        const s1 = s1Basics && BUSINESS_SNAPSHOT_QUESTIONS.every(
          (q) => (qaAns[q.key] || "").trim());
        const s2 = docsArr.some((d) => d.upload_status !== "failed");
        // Step 1 = paywall; steps 2-5 = business → docs → analyse → score/report.
        let start = 2;
        if (s1) start = 3;
        if (s1 && s2) start = 4;
        if (data?.ai_analysis) start = 5;
        // Requalifying founders (no capital purpose on record) always start at
        // the business step so they pass through the raise-purpose picker.
        if (!purposes.length && !reasons.length) start = 2;
        const openStart = start; // where they'd land with the gate open
        if (!unlocked) start = 1;
        // Flow v2: no paywall before the free score. After the free score the
        // paywall (step 1) becomes the next step unless they've paid.
        if (flowV2 && !unlocked) start = data?.free_score_used_at ? 1 : Math.min(openStart, 4);
        setWiz(start);
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("[Onboarding] initial load failed", e);
    } finally { setPageLoaded(true); }
  })(); }, []);

  const saveRole = async (role) => {
    const prev = businessRole;
    setBusinessRole(role); setRoleBusy(true);
    try {
      await api.put("/auth/me/business-role", { business_role: role });
    } catch (e) {
      setBusinessRole(prev);
      window.alert(e?.response?.data?.detail || "Couldn't save your role");
    } finally { setRoleBusy(false); }
  };

  // Debounced match-check for "same business" detection
  const [matches, setMatches] = useState([]);
  const [joinModal, setJoinModal] = useState(null); // selected match
  const [joinForm, setJoinForm] = useState({ business_role: "Co-founder", message: "" });
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinErr, setJoinErr] = useState("");
  useEffect(() => {
    const name = (snap.company_name || "").trim();
    const site = (snap.website || "").trim();
    if (name.length < 3 && !site) { setMatches([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/companies/match-check",
          { company_name: name, website: site });
        if (!cancelled) setMatches(data.matches || []);
      } catch (ex) {
        // match-check is best-effort — clear matches on failure so the UI doesn't
        // show stale "same business" hints.
        if (!cancelled) setMatches([]);
        if (process.env.NODE_ENV !== "production") console.debug("[onboarding] match-check failed:", ex?.message);
      }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [snap.company_name, snap.website]);

  const submitJoin = async () => {
    if (!joinModal) return;
    setJoinBusy(true); setJoinErr("");
    try {
      await api.post("/companies/request-join", {
        company_id: joinModal.company_id,
        business_role: joinForm.business_role,
        message: joinForm.message,
      });
      setJoinModal(null);
      // Refresh matches so the card shows "already requested"
      const { data } = await api.post("/companies/match-check",
        { company_name: snap.company_name || "", website: snap.website || "" });
      setMatches(data.matches || []);
    } catch (e) {
      setJoinErr(e.response?.data?.detail || "Couldn't submit request");
    } finally { setJoinBusy(false); }
  };

  // Posts the file with the given doc_type. `auto` flags this upload as having
  // been redirected by the filename classifier so we can show a badge afterwards.
  // Surfaces real-time progress + a sonner toast so the user always knows the
  // upload is in flight, even when the backend is doing slow OCR / vision work.
  //
  // Uses the **presigned-S3 direct-upload** flow (Feb 2026): the browser PUTs
  // bytes straight to S3, bypassing Cloudflare's ~100s edge cap that was
  // silently 524-ing large decks from slow African connections. Falls back to
  // the legacy multipart POST if the presign endpoint returns 503 (S3
  // disabled — dev / mongo backend).
  const postUpload = async (file, doc_type, { auto = false, successMessage } = {}) => {
    // Password-protected bank statements: pause and let the founder unlock
    // it (or swap in an unlocked export) via the encrypted-PDF dialog.
    if (doc_type === "bank_statements" && await pdfLooksEncrypted(file)) {
      setEncErr("");
      setEncModal({ file, docType: doc_type });
      return;
    }
    setUploadError(null);
    const okMsg = successMessage
      || (doc_type === "bank_statements" ? "Bank statement uploaded successfully." : null);
    const toastId = toast.loading(`Uploading ${file.name}…`, {
      description: "Sending file to Clinq",
      duration: Infinity,
    });
    setUploading((s) => ({ ...s, [doc_type]: { filename: file.name, progress: 0, vision: false } }));
    const setPct = (pct, desc) => {
      setUploading((s) => (s[doc_type]
        ? { ...s, [doc_type]: { ...s[doc_type], progress: pct } }
        : s));
      toast.loading(`Uploading ${file.name}… ${pct}%`, { id: toastId, description: desc });
    };
    try {
      // ── Step 1 — presign ───────────────────────────────────────────────
      let presign;
      try {
        presign = (await api.post("/documents/presign", {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          doc_type,
          size: file.size,
        })).data;
      } catch (presignErr) {
        if (presignErr?.response?.status === 503) {
          // S3 disabled → fall back to legacy multipart POST.
          const fd = new FormData(); fd.append("file", file); fd.append("doc_type", doc_type);
          const legacy = await api.post("/documents", fd, {
            timeout: 120000,
            onUploadProgress: (evt) => {
              if (!evt.total) return;
              const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
              setPct(pct, pct < 100 ? "Sending file to Clinq" : "Processing on the server…");
            },
          });
          presign = null; // signal "legacy path used"
          setDocs(d => [...d, legacy.data]);
          offerReplace(legacy.data);
          if (auto && legacy.data?.doc_id) {
            setAutoClassifiedIds(s => { const n = new Set(s); n.add(legacy.data.doc_id); return n; });
          }
          toast.success(okMsg || `${file.name} uploaded`, {
            id: toastId,
            description: legacy.data?.vision_pending
              ? "Reading your financial model in the background — this lifts your CCR Score accuracy. Safe to keep working."
              : "Saved to your document pack",
            duration: legacy.data?.vision_pending ? 6000 : 3500,
          });
          return;
        }
        throw presignErr;
      }

      // ── Step 2 — PUT bytes straight to S3 (bypasses Cloudflare) ────────
      // We use axios (not fetch) so we get onUploadProgress. `transformRequest`
      // is a no-op so axios doesn't add a Content-Type override that would
      // break the presigned URL's signed ContentType.
      const axios = (await import("axios")).default;
      await axios.put(presign.put_url, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
        timeout: 0,               // no client-side timeout — let S3 finish
        withCredentials: false,
        transformRequest: [(d) => d],
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
          setPct(pct, pct < 100 ? "Uploading to secure storage" : "Almost done — confirming…");
        },
      });

      // ── Step 3 — confirm so the server records + extracts ──────────────
      const { data: confirmed } = await api.post("/documents/confirm", {
        doc_id: presign.doc_id,
      });
      setDocs(d => [...d, confirmed]);
      offerReplace(confirmed);
      if (auto && confirmed?.doc_id) {
        setAutoClassifiedIds(s => { const n = new Set(s); n.add(confirmed.doc_id); return n; });
      }
      const isFinancialDoc = ["financial_model", "bank_statements", "mgmt_accounts"].includes(doc_type);
      const desc = isFinancialDoc
        ? "Reading your financial figures in the background — this lifts your CCR Score accuracy. Safe to keep working."
        : "Saved to your document pack";
      toast.success(okMsg || `${file.name} uploaded`, {
        id: toastId,
        description: desc,
        duration: isFinancialDoc ? 6000 : 3500,
      });
    } catch (e) {
      // Surface actionable messages so the founder knows what to do next.
      // Direct-to-S3 uploads bypass Cloudflare so we no longer see 413/524s
      // from the edge; the failure modes are now (a) CORS not configured on
      // the bucket, (b) 403 from an expired signature, or (c) network drop.
      const status = e?.response?.status;
      let msg = e?.response?.data?.detail || e?.message || "Upload failed";
      let kind = "generic";
      if (status === 401) {
        kind = "session";
        msg = "Your session has expired, so the upload couldn't be saved. " +
              "Log in again, then re-upload this file — your other documents are safe.";
      } else if (status === 400) {
        // Server sends actionable 400s (unsupported type, too large, unreadable
        // image) — show its exact wording.
        kind = "file";
      } else if (status === 413) {
        kind = "file";
        msg = "Your file exceeds our 100 MB pitch-deck limit. Try compressing " +
              "the PDF (Adobe → Compress PDF or PowerPoint → Reduce File Size) " +
              "or use the 'Having trouble? Get help' link below to reach support.";
      } else if (status === 403 || /cors/i.test(e?.message || "")) {
        msg = "Upload couldn't reach secure storage. This is usually a " +
              "one-time setup issue — please use the 'Having trouble? Get help' " +
              "link below and our team will ingest your document manually while we fix it.";
      } else if (e?.code === "ECONNABORTED" || /timeout|network/i.test(e?.message || "")) {
        msg = "Upload interrupted — your connection may be unstable. Try " +
              "again on Wi-Fi, or use the 'Having trouble? Get help' link below to reach support.";
      }
      // Persistent banner (stays until dismissed) + a brief toast.
      setUploadError({
        kind,
        title: kind === "session"
          ? "You've been signed out"
          : `Couldn't upload ${file.name}`,
        detail: msg,
      });
      toast.error(`Couldn't upload ${file.name}`, {
        id: toastId,
        description: msg,
        duration: 8000,
      });
    } finally {
      setUploading((s) => {
        const n = { ...s }; delete n[doc_type]; return n;
      });
    }
  };

  // ── Encrypted bank-statement handlers ──────────────────────────────────
  const submitEncPassword = async (password) => {
    const { file, docType } = encModal;
    setEncBusy(true); setEncErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      fd.append("pdf_password", password);
      const { data } = await api.post("/documents", fd, { timeout: 120000 });
      setDocs((d) => [...d, data]);
      setEncModal(null);
      toast.success("Bank statement uploaded successfully.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : "";
      if (code === "pdf_password_incorrect") {
        setEncErr("Incorrect password. Please try again or upload an unlocked version instead.");
      } else {
        setEncErr((detail && typeof detail === "object" ? detail.message : detail)
          || "Couldn't unlock this file. Please try again or upload an unlocked version instead.");
      }
    } finally { setEncBusy(false); }
  };

  const pickUnlockedVersion = async (file) => {
    const docType = encModal.docType;
    if (await pdfLooksEncrypted(file)) {
      setEncErr("That file still looks password-protected. Enter its password, or export an unlocked copy and try again.");
      setEncModal({ file, docType });
      return;
    }
    setEncModal(null); setEncErr("");
    await postUpload(file, docType, { successMessage: "Bank statement uploaded successfully." });
  };

  const uploadFile = async (file, doc_type) => {
    // Client-side size guard so founders get an actionable "compress" CTA
    // instead of a generic backend "File too large" error after a long upload.
    // Bumped 40 → 100 MB for pitch decks to match server (Feb 2026): real
    // African decks with team photos + high-res charts frequently hit 60-80MB.
    const capMb = doc_type === "pitch_deck" ? 100 : 25;
    if (file && file.size > capMb * 1024 * 1024) {
      showOversizedDeckToast(file, capMb);
      return;
    }
    const detected = detectDocType(file?.name);
    // Only prompt when the filename strongly suggests a DIFFERENT slot AND that
    // slot is one we actually surface to founders.
    if (detected && detected !== doc_type && ALL_DOC_TYPE_LABELS[detected]) {
      setMismatch({ file, intended: doc_type, detected });
      return;
    }
    await postUpload(file, doc_type);
  };

  // Company-profile upload for the Business Snapshot Q&A section. Bypasses
  // the filename-mismatch prompt — any document the founder considers a
  // company profile goes into the company_profile slot.
  const uploadProfileDoc = (file) => {
    if (file && file.size > 25 * 1024 * 1024) {
      showOversizedDeckToast(file, 25);
      return;
    }
    postUpload(file, "company_profile");
  };

  const removeDoc = async (doc_id) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/documents/${doc_id}`);
      setDocs(d => d.filter(x => x.doc_id !== doc_id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't remove document");
    }
  };
  // Silent cleanup of a failed presigned upload once its replacement is in.
  const discardFailedDoc = async (doc_id) => {
    try { await api.delete(`/documents/${doc_id}`); } catch (_) { /* already gone */ }
    setDocs(d => d.filter(x => x.doc_id !== doc_id));
  };

  const runAnalysis = () => analyzer.run();
  const runFreeScore = async () => {
    setFreeScoreBusy(true);
    try {
      const { data: fs } = await api.post("/founder/free-score/run");
      trackScoreComplete(fs?.score, { score_type: "free_score", verdict: fs?.likely ? "likely" : "not_yet" });
      nav("/verdict", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't score your documents — try again");
      setFreeScoreBusy(false);
    }
  };
  const freeScoreMode = flowV2 && !reportUnlocked;

  // Resume the "analysis running" screen if a background job is still going
  // (page reload / re-login while the job runs).
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || analysis) return;
    resumedRef.current = true;
    api.get("/ai/analyze/inflight").then((r) => {
      const job = r.data?.job;
      if (job?.job_id && !analysis) analyzer.resume(job.job_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Guided onboarding wizard (Jun 2026) ----
  // null = classic full page (report already downloaded / legacy done).
  const [wiz, setWiz] = useState(null);
  const [quizSnap, setQuizSnap] = useState(null);
  const [docsChecklistDone, setDocsChecklistDone] = useState(false);
  const needsDocsChecklist = !!quizSnap && quizSnap.stage === "post_revenue" && !quizSnap.docs_checklist_done && !(quizSnap.docs || []).length && !docsChecklistDone;
  const [wizNextBusy, setWizNextBusy] = useState(false);
  const wizardOn = wiz !== null;

  // Step 3 completeness, live: at least one usable document — founders
  // proceed with what they have; additional docs are optional.
  const okDocs = docs.filter((d) => d.upload_status !== "failed");
  const step2Complete = okDocs.length > 0;
  const haveDocTypes = useMemo(() => {
    const s = new Set(quizDocKeys);
    (quizSnap?.docs || []).forEach((q) => { const t = QUIZ_TO_DOC_TYPE[q]; if (t) s.add(t); });
    return s;
  }, [quizDocKeys, quizSnap?.docs]);

  // Step 1 "Next": the seven Business Snapshot questions must be answered
  // (typed or AI-prefilled) plus the three basics — verified server-side.
  // Unsaved Business Snapshot Q&A edits (reported by BusinessSnapshotQA).
  const [qaDirty, setQaDirty] = useState(false);
  useEffect(() => {
    const onDirty = (e) => setQaDirty(!!e.detail?.dirty);
    window.addEventListener("clinq:snapshot-qa-dirty", onDirty);
    return () => window.removeEventListener("clinq:snapshot-qa-dirty", onDirty);
  }, []);

  const wizStep1Next = async () => {
    if (qaDirty) {
      toast.error("Please click “Save and Continue” first", {
        description: "Your answers to the seven questions haven't been saved yet — save them, then click Next.",
        duration: 6000,
      });
      const btn = document.querySelector("[data-testid='biz-snapshot-save-btn']");
      btn?.scrollIntoView({ behavior: "smooth", block: "center" });
      btn?.classList.add("ring-4", "ring-amber-300", "animate-pulse");
      setTimeout(() => btn?.classList.remove("ring-4", "ring-amber-300", "animate-pulse"), 3000);
      return;
    }
    setWizNextBusy(true);
    try {
      const [{ data }, qaRes] = await Promise.all([
        api.get("/companies/me"),
        api.get("/companies/me/business-snapshot-qa").catch(() => ({ data: {} })),
      ]);
      const missing = [];
      if (!data?.company_name) missing.push("business name");
      if (!data?.sector) missing.push("sector");
      if (!data?.country) missing.push("country");
      if (missing.length) {
        toast.error(`Almost there — please fill in your ${missing.join(", ")} before continuing.`);
        return;
      }
      const hasPurpose = (Array.isArray(data?.capital_purposes) && data.capital_purposes.length > 0)
        || (Array.isArray(data?.raise_reasons) && data.raise_reasons.length > 0);
      if (!hasPurpose) {
        toast.error("Please select what you're raising capital for before continuing.");
        return;
      }
      const answers = qaRes.data?.answers || {};
      const unanswered = BUSINESS_SNAPSHOT_QUESTIONS.filter(
        (q) => !(answers[q.key] || "").trim());
      if (unanswered.length) {
        toast.error(unanswered.length === BUSINESS_SNAPSHOT_QUESTIONS.length
          ? "Please answer the seven questions about your business — or let us draft them from your website or company profile."
          : `Almost done — ${unanswered.length} question${unanswered.length === 1 ? "" : "s"} still need an answer.`,
          { description: "Remember to click “Save and Continue” after answering." });
        document.querySelector("[data-testid='business-snapshot-qa']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      setWiz(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_e) {
      toast.error("Couldn't verify your details — please try again.");
    } finally { setWizNextBusy(false); }
  };

  // A background analysis is running → keep the founder on Step 3 (confirmation card).
  useEffect(() => {
    if (wizardOn && (reportUnlocked || flowV2) && analyzer.submitted && wiz !== 4 && !analysis) setWiz(4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzer.submitted, wizardOn, wiz, analysis, reportUnlocked]);

  // Auto-advance to the score step the moment analysis completes.
  useEffect(() => {
    if (wizardOn && wiz === 4 && analysis) {
      setWiz(5);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [analysis, wiz, wizardOn]);

  // Tell the WhatsApp float where the founder is (pre-filled message context).
  useEffect(() => {
    if (!wizardOn) { setWaContext(snap?.company_name ? { company: snap.company_name } : null); return; }
    setWaContext({ step: wiz, total: 5, label: WIZARD_STEPS[wiz - 1]?.label, company: snap?.company_name });
    return () => setWaContext(null);
  }, [wiz, wizardOn, snap?.company_name]);

  // Paywall: steps 2-5 are unreachable until the report is unlocked
  // (flow v2: steps 2-4 open before the free score; step 5 needs the report).
  const gateOpen = reportUnlocked || (flowV2 && !freeScoreUsed);
  const wizGo = (n) => {
    if (n > 1 && !gateOpen) { setWiz(1); return; }
    if (n === 5 && !reportUnlocked) { setWiz(1); return; }
    setWiz(n); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!pageLoaded) {
    return (
      <AppShell nav={NAV.filter((n) => n.to !== "/founder/dashboard")}>
        <div className="mx-auto flex w-full max-w-4xl items-center justify-center py-32" data-testid="onboarding-loading">
          <Loader2 className="h-8 w-8 animate-spin text-[#0A8B85]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell nav={wizardOn ? NAV.filter((n) => n.to !== "/founder/dashboard") : NAV}>
      {needsDocsChecklist && (
        <FirstVisitDocsChecklist initial={quizSnap.docs || []}
          onDone={(picked) => { setDocsChecklistDone(true); setQuizSnap({ ...quizSnap, docs: picked, docs_checklist_done: true }); }} />
      )}
      {/* Breathing-room wrapper. Constrains text-heavy content to a
          comfortable reading width (max-w-4xl) and leaves bottom padding
          so the floating Clinq Assistant chat bubble never sits on top
          of primary CTAs. See /app/design_guidelines.json. */}
      <div className="mx-auto w-full max-w-4xl space-y-12 pb-32">
        {err && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700" data-testid="onboarding-error">{String(err)}</p>}

        {wizardOn && (
          <div className="space-y-6">
            {wiz > 1 && <WizardStepper current={wiz} />}
            {wiz === 1 && (
              <div data-testid="wizard-paywall-step">
                <QuizSnapshotCard quiz={quizSnap} purposes={capitalPurposes} />
                <ReportValueProp onUnlock={() => setPayOpen(true)} onTier={setUnlockTier} />
              </div>
            )}
            {wiz === 2 && justPaid && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="wizard-paid-banner">
                <p className="text-sm font-bold text-emerald-800">Payment confirmed — your Capital Readiness Report is unlocked.</p>
                <p className="mt-1 text-sm text-emerald-700">Tell us about your business below, then upload your documents. Your 3 re-run credits are ready in your account.</p>
              </div>
            )}
            {wiz === 2 && (
              <p className="rounded-xl bg-[#F4F8F8] px-4 py-3 text-sm text-neutral-700" data-testid="wizard-step1-tip">
                <span className="font-bold text-[#0A8B85]">Save time:</span> upload a company
                profile or add your business website below — we will draft the seven
                questions for you, then you just review them.
              </p>
            )}
          </div>
        )}

        {(!wizardOn || wiz === 2) && (
          <BusinessSnapshotCard
            onStageChange={setRevenueStage}
            showQA={true}
            slim={wizardOn}
            qaProfileDocs={docs.filter((d) => d.doc_type === "company_profile")}
            qaUploadingState={uploading["company_profile"]}
            onQAUpload={uploadProfileDoc}
            onQARemove={removeDoc}
          />
        )}
        {wizardOn && wiz === 2 && (
          <WizardNav onNext={wizStep1Next} busy={wizNextBusy}
            nextLabel="Next: Upload your documents" />
        )}
        {wizardOn && payOpen && unlockTier && (
          <BuyCreditsModal tier={unlockTier} returnTo="onboarding" onClose={() => setPayOpen(false)} />
        )}

        {!wizardOn && (
          <header data-testid="onboarding-hero">
            <h1 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
              Find out who can fund your business in ~5 minutes
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-neutral-600">
              Looking to raising capital? Upload the documents you currently have,
              and we&apos;ll score your business the way real funders do — then show you
              exactly who can fund you now, and/or what to fix to get funding.
            </p>
            <p className="mt-2 max-w-2xl text-sm font-semibold leading-relaxed text-neutral-700">
              The more information you&apos;re able to give us, the better the quality of your Capital Readiness Score.
            </p>
          </header>
        )}

      {/* UPLOAD WHAT YOU HAVE */}
      {(!wizardOn || wiz === 3) && (
        <section className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8" data-testid="step-deep-dive">
          <h2 className="text-lg font-bold uppercase tracking-wider text-[#0A8B85]">Upload what you have</h2>
          <p className="mt-2 text-sm leading-relaxed text-neutral-600">
            Upload the documents you have today — we review them to build your Capital Readiness Score. Additional documents are welcome but not required.
            <br/>Missing a pitch deck or financial model? <LaunchPrepButton
              testid="onboarding-prep-build-link"
              prefillCompany={snap?.company_name || ""}
              prefillSector={snap?.sector || ""}
              prefillStage={revenueStage || snap?.stage || ""}
              prefillMissingDocs={["pitch_deck", "financial_model"].filter((t) => !docs.some((d) => d.doc_type === t))}
              className="font-semibold text-[#0A8B85] hover:underline">Clinq Prep can build it for you</LaunchPrepButton>.
          </p>

          {/* Single "Good to know" callout — consolidates the previous two
              stacked banners ("Don't have every document?" + "Accuracy
              matters") into one friendly, scannable list so the page feels
              calmer for first-time founders. */}
          <div
            data-testid="upload-what-you-have-notice"
            className="mt-6 rounded-xl border border-[#0A8B85]/25 bg-[#F4F8F8] p-5"
          >
            <p className="flex items-center gap-2 text-sm font-bold text-neutral-900">
              <Sparkles className="h-4 w-4 text-[#0A8B85]"/> Good to know
            </p>
            <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-neutral-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0A8B85]"/>
                <span><b>Missing some docs?</b> Upload whatever you have today — we&apos;ll still score you and tell you exactly what to add next.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0A8B85]"/>
                <span><b>Use your latest versions.</b> The more accurate the inputs, the more credit your business gets.</span>
              </li>
            </ul>
          </div>

          {quizDocKeys.size > 0 && !quizBannerDismissed && (
            <div
              data-testid="quiz-prefill-banner"
              className="mt-4 flex items-start gap-3 rounded-xl border border-[#0A8B85]/30 bg-[#F4F8F8] p-4"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0A8B85]" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-neutral-900">
                  Welcome back — let&apos;s upload what you said you have
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-neutral-600">
                  We&apos;ve highlighted the {quizDocKeys.size} document
                  {quizDocKeys.size === 1 ? "" : "s"} you mentioned in the quiz.
                  Start with these — you can add more later.
                </p>
              </div>
              <button
                onClick={dismissQuizBanner}
                data-testid="quiz-prefill-banner-dismiss"
                aria-label="Dismiss"
                className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Persistent upload-error banner — stays until dismissed so slow
              or distracted founders never miss why an upload failed. */}
          {uploadError && (
            <div data-testid="upload-error-banner"
                 className="mt-6 flex items-start gap-3 rounded-xl border-2 border-rose-300 bg-rose-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="flex-1">
                <p className="text-sm font-bold text-rose-900">{uploadError.title}</p>
                <p className="mt-1 text-sm text-rose-800">{uploadError.detail}</p>
                {uploadError.kind === "session" && (
                  <button type="button" data-testid="upload-error-login-btn"
                          onClick={() => nav("/login")}
                          className="mt-3 rounded-md bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700">
                    Log in again
                  </button>
                )}
              </div>
              <button type="button" data-testid="upload-error-dismiss"
                      onClick={() => setUploadError(null)} aria-label="Dismiss"
                      className="rounded-md p-1 text-rose-400 hover:bg-rose-100 hover:text-rose-700">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Documents the founder said they have come FIRST, then the
              additional (optional) ones for their capital needs. The old
              fixed core block only renders as a fallback for founders with
              no capital purposes on record. */}
          {capitalPurposes.length > 0 || haveDocTypes.size > 0 ? (
            <CapitalDocChecklist purposes={capitalPurposes} docs={docs} haveIds={haveDocTypes}
              uploading={uploading} onUpload={uploadFile} onRemove={removeDoc} onDiscard={discardFailedDoc} />
          ) : (
            <div className="mt-8 space-y-3">
              {visibleCoreDocTypes.map(c => (
                <UploadCard key={c.key} cfg={c} docs={docs}
                            autoIds={autoClassifiedIds}
                            onUpload={uploadFile} onRemove={removeDoc} onDiscard={discardFailedDoc}
                            uploadingState={uploading[c.key]}
                            highlighted={quizDocKeys.has(c.key)}
                            deferrable={c.key === "mgmt_accounts" && revenueStage === "growth"}
                            deferred={!!deferrals[c.key]}
                            onDefer={(defer) => toggleDeferral(c.key, defer)}/>
              ))}
            </div>
          )}

          {/* Human fallback for founders who can't get the upload to work
              (large decks, slow connections, unusual file types, ingress
              413s, etc.). Opens the support-ticket modal pre-set to
              "Document Upload Issue" so the ops team can help — turning a
              stuck upload into a warm conversation. */}
          <TroubleUploadingCard />

          {/* Capital-unlocks section — collapsible, optional, each tile carries
              a "why this matters" blurb tied to the CRS sub-score it lifts. */}
          <div className="mt-6">
            <button
              type="button"
              data-testid="capital-unlocks-toggle"
              onClick={() => setUnlocksOpen(o => !o)}
              className="flex w-full items-center justify-between rounded-xl border border-[#0A8B85]/30 bg-[#0A8B85]/5 px-4 py-3 text-left hover:bg-[#0A8B85]/10">
              <div>
                <p className="text-sm font-bold text-[#0A8B85]">
                  Unlock more capital types {unlockedCount > 0 && <span className="ml-1 text-xs font-normal text-[#0A8B85]/80">({unlockedCount}/{extraOptionalDocTypes.length} uploaded)</span>}
                </p>
                <p className="mt-0.5 text-xs text-neutral-600">
                  Optional — each doc unlocks a specific capital type (debt, RBF, trade finance, asset finance, blended) or lifts a sub-score (DRS, ERS, RBFRS).
                </p>
              </div>
              <ArrowRight className={"h-4 w-4 text-[#0A8B85] transition-transform " + (unlocksOpen ? "rotate-90" : "")}/>
            </button>

            {unlocksOpen && (
              <div data-testid="capital-unlocks-panel"
                   className="mt-3 space-y-3">
                {extraOptionalDocTypes.map(c => (
                  <UploadCard key={c.key} cfg={c} docs={docs}
                              autoIds={autoClassifiedIds}
                              onUpload={uploadFile} onRemove={removeDoc} onDiscard={discardFailedDoc}
                              uploadingState={uploading[c.key]}
                              highlighted={quizDocKeys.has(c.key)}/>
                ))}
              </div>
            )}
          </div>

          {/* Discoverability hint for the Prep / Funder Readiness builder
              hand-off. Soft link rather than a loud CTA — keeps the page
              calm. Flows directly to prep.clinq.africa via SSO (no second
              login). */}
          <p className="mt-6 text-sm text-neutral-500">
            Don&apos;t have these documents yet?{" "}
            <LaunchPrepButton
              prefillCompany={snap?.company_name || ""}
              prefillSector={snap?.sector || ""}
              prefillStage={revenueStage || snap?.stage || ""}
              prefillMissingDocs={["pitch_deck", "financial_model"].filter((t) => !docs.some((d) => d.doc_type === t))}
              testid="onboarding-prep-hint"
              className="inline align-baseline font-semibold text-[#0A8B85] hover:underline"
            >
              Build them for free with Clinq&apos;s Funder Readiness tool →
            </LaunchPrepButton>
          </p>

          {!wizardOn && (
          <div className="mt-8 border-t border-neutral-200 pt-6">
            {analysis ? (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="run-analysis-rerun"
                  disabled={rerunLoading}
                  onClick={() => openRerun()}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50"
                >
                  {rerunLoading
                    ? <><Loader2 className="h-4 w-4 animate-spin"/> Loading…</>
                    : "Re-analyse my Documents (1 credit)"}
                </button>
                <span className="text-xs text-neutral-500">
                  Uses 1 re-run credit. Confirm your docs are updated first.
                </span>
              </div>
            ) : analyzer.submitted ? (
              <AnalysisSubmittedCard email={user?.email} />
            ) : (
              <button data-testid="run-analysis" disabled={analyzer.busy || waitingForDocs} onClick={runAnalysis}
                className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
                {(analyzer.busy || waitingForDocs)
                  ? <><Loader2 className="h-4 w-4 animate-spin"/> <span data-testid="run-analysis-phase">{waitingForDocs ? "Reading documents" : phaseLabel(analyzer.phase)}…</span></>
                  : "Analyse my Documents"}
              </button>
            )}
            <div className="mt-3">
              <GetHelpLink issueType="ai_analysis" testid="analysis-get-help" />
            </div>
          </div>
          )}

          {!wizardOn && (
          <AnalysisProgressModal
            busy={analyzer.busy || waitingForDocs}
            phase={waitingForDocs ? 0 : analyzer.phase}
            testIdPrefix="analysis"
            title={waitingForDocs ? "Reading your documents" : "Analysing your documents"}
            note={waitingForDocs
              ? "Image-based decks take 20-60 seconds to transcribe · we'll start scoring automatically"
              : null}
          />
          )}

          {rerunOpen && report && (
            <RerunModal
              checklist={report.checklist || []}
              balance={report.credits_balance || 0}
              onClose={() => setRerunOpen(false)}
              onSuccess={(payload) => {
                setRerunOpen(false);
                // Push the fresh CCR analysis into the onboarding state so
                // the inline ReadinessReportCard below re-renders with the
                // new score. Also nudge AppShell to re-fetch credits so the
                // header chip is instantly accurate.
                if (payload?.analysis) setAnalysis(payload.analysis);
                setReport((r) => r ? {
                  ...r,
                  readiness_score: payload.new_score ?? r.readiness_score,
                  summary: payload.analysis?.summary ?? r.summary,
                  strengths: payload.analysis?.strengths || r.strengths || [],
                  weaknesses: payload.analysis?.weaknesses || r.weaknesses || [],
                  roadmap: payload.analysis?.roadmap || r.roadmap || [],
                  checklist: payload.checklist ?? r.checklist,
                  credits_balance: payload.credits_remaining ?? r.credits_balance,
                } : r);
                try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ }
              }}
            />
          )}
          {buyOpen && report && (
            <BuyCreditsModal
              tier={report.tier}
              onClose={() => setBuyOpen(false)}
            />
          )}

          {!wizardOn && analysis && (
            <ReadinessReportCard variant="onboarding" onContinue={() => nav("/founder/dashboard")} />
          )}
        </section>
      )}

      {/* Wizard step 2 nav + steps 3-5 */}
      {wizardOn && wiz === 3 && (
        <WizardNav onBack={() => wizGo(2)} onNext={() => wizGo(4)}
          nextLabel={freeScoreMode ? "Next: Get my free score" : "Next: Run my Capital Readiness Score"}
          nextDisabled={!step2Complete}
          hint="Upload at least one document to continue — the more you upload, the more accurate your score." />
      )}

      {wizardOn && wiz === 4 && (
        <>
          <section className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8" data-testid="wizard-analysis-step">
            <h2 className="text-lg font-bold uppercase tracking-wider text-[#0A8B85]">
              {freeScoreMode ? "Your free documentation score" : "Generating your Capital Readiness Score and Report"}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-600">
              {freeScoreMode
                ? "We score your business on the documents you've provided and tell you straight away whether funders on our platform are likely to fund you. One free score — the full verified analysis comes with your report."
                : "We are reviewing your documents and scoring your business the way real funders do. This usually takes a few minutes."}
            </p>
            <div className="mt-6">
              {freeScoreMode ? (
                freeScoreUsed ? (
                  <button data-testid="view-free-score" onClick={() => nav("/verdict")}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-6 py-3 text-sm font-bold text-white hover:bg-[#066667]">View my score</button>
                ) : (
                  <button data-testid="run-free-score" disabled={freeScoreBusy || waitingForDocs} onClick={runFreeScore}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#F59E0B] px-6 py-3 text-sm font-bold text-[#04302E] hover:bg-amber-400 disabled:opacity-50">
                    {(freeScoreBusy || waitingForDocs) ? <><Loader2 className="h-4 w-4 animate-spin"/> {waitingForDocs ? "Reading documents…" : "Scoring your documents…"}</> : "Get my free score"}
                  </button>
                )
              ) : analyzer.submitted ? <AnalysisSubmittedCard email={user?.email} /> : (
              <button data-testid="run-analysis" disabled={analyzer.busy || waitingForDocs} onClick={runAnalysis}
                className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-6 py-3 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                {(analyzer.busy || waitingForDocs)
                  ? <><Loader2 className="h-4 w-4 animate-spin"/> <span data-testid="run-analysis-phase">{waitingForDocs ? "Reading documents" : phaseLabel(analyzer.phase)}…</span></>
                  : "Analyse my Documents"}
              </button>
              )}
              <div className="mt-3">
                <GetHelpLink issueType="ai_analysis" testid="analysis-get-help-wizard" />
              </div>
            </div>
          </section>
          <WizardNav onBack={() => wizGo(3)} />
        </>
      )}

      {wizardOn && wiz === 5 && (
        <>
          <ReadinessReportCard variant="onboarding" />
          <ReportDownloadStep />
          <WizardNav onBack={() => wizGo(4)} />
        </>
      )}

      {/* Progress modal must live at page level so it renders on wizard
          step 3 too (the upload section only mounts on step 2). */}
      {wizardOn && (
        <AnalysisProgressModal
          busy={analyzer.busy || waitingForDocs}
          phase={waitingForDocs ? 0 : analyzer.phase}
          testIdPrefix="analysis"
          title={waitingForDocs ? "Reading your documents" : "Analysing your documents"}
          note={waitingForDocs
            ? "Image-based decks take 20-60 seconds to transcribe · we'll start scoring automatically"
            : null}
        />
      )}

      {/* AI Snapshot modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ai-snapshot-modal">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-neutral-200 bg-white px-6 py-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#0A8B85]"/>
                <h2 className="text-lg font-bold text-neutral-900">Auto-fill from your pitch</h2>
              </div>
              <button onClick={()=>{setAiOpen(false); setAiSugg(null); setAiErr("");}} data-testid="ai-snapshot-close" className="rounded p-1 hover:bg-neutral-100"><X className="h-4 w-4"/></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-neutral-600">Upload your pitch deck or paste a description. We will pick out your sector, country, stage, ticket band, revenue, value proposition and SDGs.</p>

              <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-300 bg-neutral-50 px-6 py-6 hover:border-[#0A8B85] hover:bg-[#F4F8F8]" data-testid="ai-snap-upload">
                <Upload className="h-5 w-5 text-[#0A8B85]"/>
                <p className="mt-2 text-sm font-semibold text-neutral-900">{aiFile ? aiFile.name : "Upload pitch deck (PDF)"}</p>
                <p className="text-xs text-neutral-500">Optional · max 40MB</p>
                <input type="file" accept=".pdf" className="hidden" data-testid="ai-snap-file"
                  onChange={e=>setAiFile(e.target.files?.[0] || null)}/>
              </label>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">Or paste a description</label>
                <textarea data-testid="ai-snap-text" rows={6} value={aiText}
                  onChange={e=>setAiText(e.target.value)}
                  placeholder="e.g. PayLagos is a fintech startup in Lagos. We provide cross-border payments for African SMBs. $850K ARR, 24 employees, raising $1.2-2.5M Series A. Focus on financial inclusion (SDG 8)…"
                  className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85] resize-none"/>
              </div>

              {aiErr && <p className="text-sm text-red-600" data-testid="ai-snap-error">{String(aiErr)}</p>}

              <div className="flex justify-end">
                <button type="button" data-testid="ai-snap-run" disabled={aiBusy || (!aiText.trim() && !aiFile)} onClick={runAiSnap}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
                  {aiBusy ? <><Loader2 className="h-4 w-4 animate-spin"/>Extracting…</> : <><Sparkles className="h-4 w-4"/>Extract snapshot</>}
                </button>
              </div>

              {aiSugg && (
                <div className="mt-2 rounded-xl bg-[#F4F8F8] p-5" data-testid="ai-snap-result">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">What we found</p>
                      <p className="mt-1 text-base font-bold text-neutral-900">{aiSugg.company_name || "(unnamed)"}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {aiSugg._extracted_via === "haiku-4-5" && (
                        <span
                          data-testid="ai-snap-fast-badge"
                          title="Sonnet 4.5 was congested — we used Haiku 4.5 for a faster response."
                          className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-700"
                        >
                          Fast mode
                        </span>
                      )}
                      <span className="rounded-full bg-[#0A8B85]/10 px-2.5 py-0.5 text-xs font-semibold text-[#0A8B85]">Confidence {aiSugg.confidence}</span>
                    </div>
                  </div>
                  {aiSugg.summary && <p className="mt-2 text-sm text-neutral-700">{aiSugg.summary}</p>}
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                    <Pill label="Sector" v={aiSugg.sector}/>
                    <Pill label="Country" v={aiSugg.country}/>
                    <Pill label="Stage" v={aiSugg.stage}/>
                    <Pill label="Employees" v={aiSugg.employees}/>
                    <Pill label="Revenue" v={`$${(aiSugg.revenue_annual/1000).toFixed(0)}K`}/>
                    <Pill label="Ticket min" v={`$${(aiSugg.ticket_min/1000).toFixed(0)}K`}/>
                    <Pill label="Ticket max" v={`$${(aiSugg.ticket_max/1000).toFixed(0)}K`}/>
                    <Pill label="SDGs" v={(aiSugg.sdgs||[]).join(", ") || "—"}/>
                  </div>
                  {aiSugg.value_prop && (
                    <div className="mt-3 rounded-lg bg-white p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Value prop</p>
                      <p className="mt-1 text-sm text-neutral-800">{aiSugg.value_prop}</p>
                    </div>
                  )}
                  <div className="mt-5 flex justify-end gap-2">
                    <button onClick={()=>setAiSugg(null)} className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Discard</button>
                    <button data-testid="ai-snap-apply" onClick={applyAiSnap} className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-semibold text-white hover:bg-[#066667]">Apply to form</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Join request modal */}
      {joinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="join-modal">
          <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-neutral-900">Request to join {joinModal.company_name}</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  We&apos;ll send your request to {joinModal.primary_name || "the primary founder"} for approval.
                </p>
              </div>
              <button onClick={() => setJoinModal(null)} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100">
                <X className="h-5 w-5"/>
              </button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Your role in the business</label>
                <select data-testid="join-role" value={joinForm.business_role}
                  onChange={e => setJoinForm({...joinForm, business_role: e.target.value})}
                  className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A8B85]">
                  {["Founder","Co-founder","CEO","CFO","COO","CTO","Head of Product","Board member","Advisor","Other"].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Note (optional)</label>
                <textarea data-testid="join-message" rows={3} value={joinForm.message}
                  onChange={e => setJoinForm({...joinForm, message: e.target.value})}
                  placeholder="A quick hello so they recognise you…"
                  className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"/>
              </div>
              {joinErr && <p className="text-sm text-red-600">{joinErr}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={() => setJoinModal(null)} className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button>
                <button onClick={submitJoin} disabled={joinBusy} data-testid="join-submit"
                  className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                  {joinBusy ? "Sending…" : "Send request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filename-mismatch confirmation dialog */}
      {mismatch && (
        <div
          data-testid="doc-mismatch-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#0A8B85]"/>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-neutral-900">
                  This looks like a different document type
                </h3>
                <p className="mt-1 text-sm text-neutral-600">
                  Based on the filename
                  <span className="mx-1 rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs text-neutral-700">
                    {mismatch.file?.name || ""}
                  </span>
                  this might belong in
                  <span className="font-semibold text-[#0A8B85]"> {ALL_DOC_TYPE_LABELS[mismatch.detected]}</span>
                  &nbsp;instead of
                  <span className="font-semibold text-neutral-700"> {ALL_DOC_TYPE_LABELS[mismatch.intended]}</span>.
                </p>
                <p className="mt-2 text-xs text-neutral-500">
                  Choosing the right slot helps us read the strongest signals from your documents.
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                data-testid="doc-mismatch-keep"
                onClick={async () => {
                  const m = mismatch;
                  setMismatch(null);
                  await postUpload(m.file, m.intended);
                }}
                className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-200">
                Keep in {ALL_DOC_TYPE_LABELS[mismatch.intended]}
              </button>
              <button
                data-testid="doc-mismatch-use-detected"
                onClick={async () => {
                  const m = mismatch;
                  setMismatch(null);
                  if (UNLOCK_DOC_TYPES.some(c => c.key === m.detected)) {
                    setUnlocksOpen(true);
                  }
                  await postUpload(m.file, m.detected, { auto: true });
                }}
                className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#066667]">
                Upload to {ALL_DOC_TYPE_LABELS[mismatch.detected]}
              </button>
            </div>
          </div>
        </div>
      )}
      {replacePrompt && (
        <ReplaceOldVersionModal fresh={replacePrompt.fresh} olds={replacePrompt.olds} busy={replacing}
          onKeep={() => setReplacePrompt(null)} onReplace={replaceOld} />
      )}
      {encModal && (
        <EncryptedPdfDialog
          filename={encModal.file?.name}
          busy={encBusy}
          error={encErr}
          onSubmitPassword={submitEncPassword}
          onPickUnlocked={pickUnlockedVersion}
          onClose={() => { if (!encBusy) { setEncModal(null); setEncErr(""); } }}
        />
      )}
      </div>
    </AppShell>
  );
}

const inp = "w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A8B85]";
const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
    {children}
  </div>
);
const Pill = ({ label, v }) => (
  <div className="rounded-lg bg-white p-2.5">
    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
    <p className="mt-0.5 font-semibold text-neutral-900">{v ?? "—"}</p>
  </div>
);

// Get Started quiz draft (see GetStartedQuiz.jsx). Used to auto-fill the
// founder's revenue stage from their Step 1 (stage) + Step 2 (KES band)
// quiz answers when they land here without a saved stage.
const QUIZ_STORAGE_KEY = "clinq_get_started_v2";
// Bands at KES 5M+/month cross ~KES 50M/yr → "growth".
const QUIZ_GROWTH_BANDS = new Set(["5m_15m", "gt_15m"]);
const stageFromQuizDraft = () => {
  try {
    const raw = localStorage.getItem(QUIZ_STORAGE_KEY);
    if (!raw) return "";
    const obj = JSON.parse(raw);
    if (obj?.stage === "pre_revenue") return "pre_revenue";
    if (obj?.stage === "post_revenue")
      return QUIZ_GROWTH_BANDS.has(obj?.revenueBand) ? "growth" : "early_revenue";
  } catch (_) { /* ignore malformed drafts */ }
  return "";
};

const SNAPSHOT_SECTORS = [
  "Agriculture / AgriTech",
  "Healthcare / HealthTech",
  "Financial Services / FinTech",
  "Manufacturing / Industrials",
  "Retail / Consumer Goods",
  "Logistics & Supply Chain",
  "Energy / CleanTech",
  "Real Estate / Construction",
  "Technology / SaaS",
  "Education / EdTech",
  "Hospitality / Tourism",
];

// Fuzzy-map a stored sector string (e.g. "Fintech", "Real Estate") onto one
// of the dropdown categories; returns null when nothing fits ("Other").
function matchSnapshotSector(val) {
  const v = (val || "").trim().toLowerCase();
  if (!v) return null;
  for (const cat of SNAPSHOT_SECTORS) {
    if (cat.toLowerCase() === v) return cat;
    const tokens = cat.toLowerCase().split(/[/&]/).map((t) => t.trim());
    if (tokens.some((t) => t === v || t.includes(v) || v.includes(t))) return cat;
  }
  return null;
}

const SNAPSHOT_COUNTRIES = [
  "Kenya", "Nigeria", "Ghana", "South Africa", "Egypt",
  "Rwanda", "Senegal", "Ethiopia", "Tanzania", "Uganda",
];

function BusinessSnapshotCard({ onStageChange, showQA, slim = false, qaProfileDocs, qaUploadingState, onQAUpload, onQARemove }) {
  const [selected, setSelected] = useState("");
  const [glossary, setGlossary] = useState(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Jun 2026: Step 1 replaced by the Business Snapshot — self-populated from
  // the Get Started quiz, post-signup Welcome answers and AI website
  // enrichment. Everything stays editable inline.
  const [businessRole, setBusinessRole] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);
  const [website, setWebsite] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [linksSaving, setLinksSaving] = useState(false);
  const [basics, setBasics] = useState({ company_name: "", sector: "", business_summary: "", country: "" });
  const [basicsSaving, setBasicsSaving] = useState(false);
  // Raise purpose (Jun 2026) — prefilled from the Welcome page, editable here.
  const [raiseReasons, setRaiseReasons] = useState([]);
  const [raiseOther, setRaiseOther] = useState("");
  const [snapPurposes, setSnapPurposes] = useState([]);
  const [raiseSaving, setRaiseSaving] = useState(false);
  const raiseDirty = useRef(false);
  const [enriching, setEnriching] = useState(false);
  const basicsDirty = useRef(false);
  const setBasic = (k, v) => { basicsDirty.current = true; setBasics(b => ({ ...b, [k]: v })); };
  // Sector dropdown: "" (unset) | one of SNAPSHOT_SECTORS | "__other"
  const [sectorSel, setSectorSel] = useState("");
  const syncSectorSel = (val) => {
    const v = (val || "").trim();
    if (!v) { setSectorSel(""); return; }
    setSectorSel(matchSnapshotSector(v) || "__other");
  };
  // HQ country dropdown: "" | one of SNAPSHOT_COUNTRIES | "__other"
  const [countrySel, setCountrySel] = useState("");
  const syncCountrySel = (val) => {
    const v = (val || "").trim();
    if (!v) { setCountrySel(""); return; }
    const hit = SNAPSHOT_COUNTRIES.find((c) => c.toLowerCase() === v.toLowerCase());
    setCountrySel(hit || "__other");
  };
  const onCountrySelect = (v) => {
    setCountrySel(v);
    setBasic("country", v === "__other" ? "" : v);
  };
  const onSectorSelect = (v) => {
    setSectorSel(v);
    setBasic("sector", v === "__other" ? "" : v);
  };

  useEffect(() => {
    (async () => {
      // Use allSettled so ONE flaky sibling endpoint (e.g. glossary hiccups on
      // a fresh account) can't wipe the entire init and leave the tile
      // showing empty website / linkedin / business-role state on first paint.
      const [meRes, gRes, meUserRes] = await Promise.allSettled([
        api.get("/companies/me"),
        api.get("/glossary/revenue-stage"),
        api.get("/auth/me"),
      ]);
      if (meRes.status === "fulfilled") {
        const stage = meRes.value.data?.revenue_stage || "";
        if (stage) {
          setSelected(stage);
          onStageChange?.(stage);
        } else {
          // No stage saved yet — mimic the founder's Get Started quiz
          // answers (Step 1 + Step 2) and persist silently. They can
          // still change it by clicking another option.
          const quizStage = stageFromQuizDraft();
          if (quizStage) {
            setSelected(quizStage);
            onStageChange?.(quizStage);
            api.put("/companies/me/revenue-stage", { revenue_stage: quizStage }).catch((err) => {
              if (process.env.NODE_ENV === "development") console.warn("[RevenueStage] quiz prefill save failed", err);
            });
          }
        }
        setWebsite(meRes.value.data?.website || "");
        setLinkedin(meRes.value.data?.linkedin_url || "");
        const c = meRes.value.data || {};
        if (!raiseDirty.current) {
          setRaiseReasons(c.raise_reasons || []);
          setRaiseOther(c.raise_reason_other || "");
        }
        setSnapPurposes(c.capital_purposes || []);
        // Don't clobber values the founder already started typing while the
        // fetch was in flight.
        if (!basicsDirty.current) {
          setBasics({
            company_name: c.company_name || "",
            sector: c.sector || "",
            business_summary: c.business_summary || "",
            country: c.country || "",
          });
          syncSectorSel(c.sector);
          syncCountrySel(c.country);
        }
        // AI enrichment from website (or LinkedIn page when no website) —
        // only when at least one snapshot field is still blank.
        if (((c.website || "").trim() || (c.linkedin_url || "").trim()) &&
            (!(c.company_name || "").trim() || !(c.sector || "").trim() || !(c.business_summary || "").trim())) {
          setEnriching(true);
          api.post("/companies/me/snapshot/enrich-from-website", {})
            .then(({ data }) => {
              if (data?.ok) {
                setBasics((prev) => {
                  const merged = {
                    company_name: prev.company_name || data.company_name || "",
                    sector: prev.sector || data.sector || "",
                    business_summary: prev.business_summary || data.business_summary || "",
                    country: prev.country || data.country || "",
                  };
                  syncSectorSel(merged.sector);
                  syncCountrySel(merged.country);
                  return merged;
                });
              }
            })
            .catch(() => { /* best-effort */ })
            .finally(() => setEnriching(false));
        }
      }
      if (meUserRes.status === "fulfilled") {
        setBusinessRole(meUserRes.value.data?.business_role || "");
      }
      if (gRes.status === "fulfilled") {
        setGlossary(gRes.value.data);
      }
      if (process.env.NODE_ENV === "development") {
        for (const [name, res] of [["/companies/me", meRes], ["/glossary/revenue-stage", gRes], ["/auth/me", meUserRes]]) {
          if (res.status === "rejected") console.warn(`[RevenueStage] ${name} init failed`, res.reason);
        }
      }
    })();
  }, [onStageChange]);

  const set = async (stage) => {
    setBusy(true);
    try {
      await api.put("/companies/me/revenue-stage", { revenue_stage: stage });
      setSelected(stage);
      onStageChange?.(stage);
    } finally { setBusy(false); }
  };

  const saveRole = async (r) => {
    setBusinessRole(r);
    setRoleSaving(true);
    try { await api.put("/auth/me/business-role", { business_role: r }); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("[RevenueStage] role save failed", err);
    } finally { setRoleSaving(false); }
  };

  // Persist website / LinkedIn 700ms after the last keystroke — avoids
  // hammering the backend on every character.
  useEffect(() => {
    const handle = setTimeout(async () => {
      // Only PUT once the founder has actually typed something — initial
      // page-load values from /companies/me triggering an empty PUT is wasteful.
      if (!website && !linkedin) return;
      setLinksSaving(true);
      try { await api.put("/companies/me/profile-links", { website, linkedin_url: linkedin }); }
      catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[RevenueStage] links save failed", err);
      } finally { setLinksSaving(false); }
    }, 700);
    return () => clearTimeout(handle);
  }, [website, linkedin]);

  // Persist name / industry / summary 700ms after the last keystroke —
  // only once the founder has actually edited something (dirty flag).
  useEffect(() => {
    if (!basicsDirty.current) return;
    const handle = setTimeout(async () => {
      setBasicsSaving(true);
      try { await api.put("/companies/me/snapshot-basics", basics); }
      catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[Snapshot] basics save failed", err);
      } finally { setBasicsSaving(false); }
    }, 700);
    return () => clearTimeout(handle);
  }, [basics]);

  // Persist raise purpose 700ms after the last change — only once the
  // founder edited it AND the answer is valid (>=1 reason + funds text).
  useEffect(() => {
    if (!raiseDirty.current) return;
    const handle = setTimeout(async () => {
      if (raiseReasons.length === 0) return;
      if (raiseReasons.includes("other") && !raiseOther.trim()) return;
      setRaiseSaving(true);
      try {
        await api.put("/companies/me/raise-purpose", {
          raise_reasons: raiseReasons,
          raise_reason_other: raiseOther.trim(),
        });
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[Snapshot] raise-purpose save failed", err);
      } finally { setRaiseSaving(false); }
    }, 700);
    return () => clearTimeout(handle);
  }, [raiseReasons, raiseOther]);

  const opts = [
    { key: "pre_revenue", label: "Pre-revenue", hint: "KES 0 – 10M per annum" },
    { key: "early_revenue", label: "Early revenue", hint: "KES 10M – 50M per annum" },
    { key: "growth", label: "Growth", hint: "Over KES 50M per annum" },
  ];

  // Role labels mirror BUSINESS_ROLES in /app/backend/core/constants.py
  const roleOpts = [
    { key: "Founder", label: "Founder" },
    { key: "Co-founder", label: "Co-founder" },
    { key: "CEO", label: "CEO" },
    { key: "CFO", label: "CFO" },
    { key: "COO", label: "COO" },
    { key: "CTO", label: "CTO" },
    { key: "Advisor", label: "Advisor" },
    { key: "Other", label: "Other" },
  ];

  return (
    <div className="mt-6 rounded-2xl border border-[#0A8B85]/30 bg-[#E3F4EE] p-5" data-testid="revenue-stage-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-lg font-bold uppercase tracking-wider text-[#0A8B85]">{slim ? "Tell us about your business" : "Business Snapshot"}</p>
          <p className="mt-1 text-sm text-neutral-700">
            {slim
              ? "Answer the seven questions below — or upload a company profile / add your website and we will draft them for you."
              : "We've pre-filled what we know from your answers and website — review and edit anything that's off."}
          </p>
        </div>
        <button onClick={() => setGlossaryOpen(true)} data-testid="revenue-stage-learn"
          className="text-xs font-semibold text-[#0A8B85] hover:underline">
          Learn what these mean →
        </button>
      </div>
      {enriching && (
        <p className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#0A8B85]" data-testid="snapshot-enriching">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading your website / LinkedIn page to fill in the blanks…
        </p>
      )}

      {/* Business name + Industry */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold text-neutral-700" htmlFor="snapshot-business-name">Business name</label>
          <input
            id="snapshot-business-name"
            data-testid="snapshot-business-name"
            type="text"
            placeholder="Your company name"
            value={basics.company_name}
            onChange={(e) => setBasic("company_name", e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-[#0A8B85] focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-700" htmlFor="snapshot-sector-select">What sector is your business in?</label>
          <select
            id="snapshot-sector-select"
            data-testid="snapshot-sector-select"
            value={sectorSel}
            onChange={(e) => onSectorSelect(e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-[#0A8B85] focus:outline-none"
          >
            <option value="">Select a sector…</option>
            {SNAPSHOT_SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value="__other">Other</option>
          </select>
          {sectorSel === "__other" && (
            <input
              data-testid="snapshot-sector-other"
              type="text"
              placeholder="Tell us your sector"
              value={basics.sector}
              onChange={(e) => setBasic("sector", e.target.value)}
              className="mt-2 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-[#0A8B85] focus:outline-none"
            />
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-700" htmlFor="snapshot-country-select">Business headquarters</label>
          <select
            id="snapshot-country-select"
            data-testid="snapshot-country-select"
            value={countrySel}
            onChange={(e) => onCountrySelect(e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-[#0A8B85] focus:outline-none"
          >
            <option value="">Where is the business domiciled?</option>
            {SNAPSHOT_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="__other">Other</option>
          </select>
          {countrySel === "__other" && (
            <input
              data-testid="snapshot-country-other"
              type="text"
              placeholder="Tell us the country"
              value={basics.country}
              onChange={(e) => setBasic("country", e.target.value)}
              className="mt-2 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-[#0A8B85] focus:outline-none"
            />
          )}
        </div>
      </div>

      {!slim && (
      <>
      <div className="mt-5">
        <p className="text-sm font-semibold text-neutral-900">Is your business pre-revenue or post-revenue?</p>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {opts.map(o => (
          <button key={o.key} onClick={() => set(o.key)} disabled={busy}
            data-testid={`revenue-stage-${o.key}`}
            className={"rounded-xl border-2 px-4 py-3 text-left transition-colors disabled:opacity-50 " +
              (selected === o.key
                ? "border-[#0A8B85] bg-white"
                : "border-neutral-200 bg-white hover:border-[#0A8B85]/50")}>
            <p className="text-sm font-bold text-neutral-900">{o.label}</p>
            <p className="text-xs text-neutral-500">{o.hint}</p>
          </button>
        ))}
        </div>
      </div>

      {/* Role in business — single-line select chip row */}
      <div className="mt-5">
        <p className="text-sm font-semibold text-neutral-900">What&apos;s your role in the business?</p>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="business-role-picker">
          {roleOpts.map((r) => (
            <button key={r.key}
              type="button"
              onClick={() => saveRole(r.key)}
              disabled={roleSaving}
              data-testid={`business-role-${r.key}`}
              className={"rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 " +
                (businessRole === r.key
                  ? "border-[#0A8B85] bg-[#0A8B85] text-white"
                  : "border-neutral-300 bg-white text-neutral-700 hover:border-[#0A8B85]/60")}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      </>
      )}

      {/* Why raising capital — mirrors the Welcome page question (Jun 2026).
          Also shown in wizard (slim) mode when the founder has no capital
          purpose on record, so requalifying legacy founders can classify
          their raise without leaving step 1. */}
      {(!slim || snapPurposes.length === 0) && (
      <div className="mt-5" data-testid="snapshot-raise-purpose">
        <RaisePurposePicker
          reasons={raiseReasons}
          setReasons={(v) => { raiseDirty.current = true; setRaiseReasons(v); }}
          other={raiseOther}
          setOther={(v) => { raiseDirty.current = true; setRaiseOther(v); }}
          quizPurposes={snapPurposes}
        />
        {raiseSaving && <p className="mt-1 text-[11px] text-neutral-500">Saving…</p>}
      </div>
      )}

      {/* Website + LinkedIn — pre-filled from the post-signup Welcome page. */}
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold text-neutral-700" htmlFor="onb-website">Website</label>
          <input
            id="onb-website"
            data-testid="onb-website"
            type="url"
            placeholder="https://yourcompany.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-[#0A8B85] focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-700" htmlFor="onb-linkedin">LinkedIn</label>
          <input
            id="onb-linkedin"
            data-testid="onb-linkedin"
            type="url"
            placeholder="https://linkedin.com/company/…"
            value={linkedin}
            onChange={(e) => setLinkedin(e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-[#0A8B85] focus:outline-none"
          />
        </div>
        {linksSaving && (
          <p className="col-span-full text-[11px] text-neutral-500">Saving…</p>
        )}
      </div>

      {/* Business summary textarea removed (Jun 2026) — Q&A question 1
          ("What does your business do…") captures this for everyone now. */}
      {basicsSaving && <p className="mt-2 text-[11px] text-neutral-500">Saving…</p>}

      {/* Qualitative Q&A — shown for ALL founders (Jun 2026; previously only
          without a pitch deck). 7 free-text questions + optional
          company-profile doc, feeding the CRS analysis and admin pipeline. */}
      {showQA && (
        <BusinessSnapshotQA
          profileDocs={qaProfileDocs}
          uploadingState={qaUploadingState}
          onUpload={onQAUpload}
          onRemove={onQARemove}
        />
      )}

      {glossaryOpen && glossary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="revenue-stage-glossary">
          <div className="w-full max-w-lg rounded-2xl border border-neutral-200 bg-white p-6">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold text-neutral-900">Revenue-stage glossary</h3>
              <button onClick={() => setGlossaryOpen(false)} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5"/></button>
            </div>
            <dl className="mt-4 space-y-4">
              {Object.entries(glossary).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-sm font-bold text-[#0A8B85]">{v.label}</dt>
                  <dd className="mt-1 text-sm text-neutral-700">{v.definition}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}


function UploadCard({ cfg, docs, onUpload, onRemove, onDiscard, autoIds, uploadingState, highlighted = false,
                      deferrable = false, deferred = false, onDefer }) {
  const uploaded = docs.filter(d => d.doc_type === cfg.key);
  const hasFiles = uploaded.length > 0;
  const isUploading = !!uploadingState;
  const isFinancialModel = cfg.key === "financial_model";
  // Failed doc being replaced via "Upload again" — discarded once the new file lands.
  const retryDocRef = useRef(null);
  // When the founder ticked this doc in the public Get-Started quiz we want
  // to draw the eye to the card so they remember why they're here. Hides as
  // soon as they actually upload (then the green check takes over).
  const showQuizBadge = highlighted && !hasFiles;
  // "Why this matters" used to render as a permanent amber block — visually
  // noisy on a page with 4+ tiles. Tuck it behind a small toggle so the
  // info is still discoverable but the page reads as calmer at first glance.
  const [showWhy, setShowWhy] = useState(false);
  // Each tile needs a stable input id so the visible primary button can
  // drive the (hidden, native) file picker via <label htmlFor>. Founders
  // reported they didn't realise the whole card was clickable — an explicit
  // "Upload file" pill fixes that discoverability gap without breaking the
  // existing card-click flow.
  const inputId = `upload-input-${cfg.key}`;
  const buttonLabel = isUploading
    ? "Uploading…"
    : hasFiles
      ? "Add another"
      : "Upload file";
  return (
    <div
      data-testid={`upload-${cfg.key}`}
      data-quiz-highlighted={showQuizBadge ? "1" : undefined}
      className={
        "flex flex-col gap-2 rounded-xl border-2 border-dashed bg-neutral-50 px-4 py-5 transition-colors " +
        (isUploading
          ? "border-[#00828F] bg-[#E0F3F5]"
          : showQuizBadge
            ? "border-amber-400 bg-amber-50/60 ring-2 ring-amber-300/50 hover:bg-amber-50"
            : "border-neutral-300 hover:border-[#00828F] hover:bg-[#E0F3F5]")
      }>
      {showQuizBadge && (
        <span
          data-testid={`upload-${cfg.key}-quiz-badge`}
          className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-400/90 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-neutral-900"
        >
          From your quiz
        </span>
      )}
      <div className="flex items-start gap-2">
        {isUploading
          ? <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#00828F]" data-testid={`upload-${cfg.key}-spinner`}/>
          : <Upload className="mt-0.5 h-4 w-4 shrink-0 text-[#00828F]"/>}
        <div className="min-w-0 flex-1">
          <span className="text-sm font-bold text-neutral-900">{cfg.label}</span>
          {hasFiles && <CheckCircle2 className="ml-1 inline h-4 w-4 text-green-600"/>}
          <span className="block text-xs text-neutral-500">{cfg.hint}</span>
          {["pitch_deck", "financial_model"].includes(cfg.key) && !hasFiles && (
            <span className="mt-0.5 block text-[11px] font-semibold text-[#0A8B85]"
              data-testid={`upload-${cfg.key}-build-help`}>
              Don&apos;t have one yet? Clinq can help you build it — chat with us on WhatsApp or email capital@clinq.africa.
            </span>
          )}
          {cfg.why && (
            <>
              <button
                type="button"
                data-testid={`upload-${cfg.key}-why-toggle`}
                onClick={() => setShowWhy(o => !o)}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#00828F] hover:underline"
              >
                {showWhy ? "Hide" : "Why funders care"}
                <ArrowRight className={"h-3 w-3 transition-transform " + (showWhy ? "rotate-90" : "")}/>
              </button>
              {showWhy && (
                <span
                  data-testid={`upload-${cfg.key}-why`}
                  className="mt-1.5 block rounded-md bg-[#00828F]/10 px-2 py-1 text-[11px] leading-snug text-[#00828F]">
                  {cfg.why}
                </span>
              )}
            </>
          )}
        </div>
        {/* Explicit upload button — the primary affordance on the card. The
            native input is hidden and its click is driven by <label htmlFor>
            so keyboard tab-through / a11y remain intact. */}
        <label
          htmlFor={inputId}
          data-testid={`upload-${cfg.key}-btn`}
          aria-disabled={isUploading || undefined}
          className={
            "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold shadow-sm transition " +
            (isUploading
              ? "cursor-not-allowed bg-neutral-200 text-neutral-500"
              : hasFiles
                ? "cursor-pointer border border-[#004B62]/30 bg-white text-[#004B62] hover:border-[#004B62] hover:bg-[#E0F3F5]"
                : "cursor-pointer bg-[#004B62] text-white hover:bg-[#00303F]")
          }
        >
          {isUploading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin"/>
            : <Upload className="h-3.5 w-3.5"/>}
          {buttonLabel}
        </label>
        <input
          id={inputId}
          type="file"
          multiple
          accept=".pdf,.pptx,.xlsx,.xlsm,.csv,.docx,.txt,.md,.jpg,.jpeg,.png,.heic,.heif"
          className="hidden"
          disabled={isUploading}
          onChange={async (e) => {
            const picked = Array.from(e.target.files || []);
            e.target.value = "";
            const retryId = retryDocRef.current;
            retryDocRef.current = null;
            for (const f of picked) await onUpload(f, cfg.key);
            if (retryId && picked.length && onDiscard) await onDiscard(retryId);
          }}
        />
      </div>

      {/* "I don't have these yet" — growth-stage mgmt accounts only. Counts
          the step complete while flagging the gap for later follow-up. */}
      {deferrable && !hasFiles && !isUploading && (
        deferred ? (
          <div data-testid={`upload-${cfg.key}-deferred`}
               className="mt-1 flex flex-wrap items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-neutral-700">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
            <span>Marked as &ldquo;don&apos;t have these yet&rdquo; — flagged so you can add them later.</span>
            <button type="button" data-testid={`upload-${cfg.key}-defer-undo`}
                    onClick={() => onDefer(false)}
                    className="font-bold text-[#00828F] hover:underline">
              Undo
            </button>
          </div>
        ) : (
          <button type="button" data-testid={`upload-${cfg.key}-defer-btn`}
                  onClick={() => onDefer(true)}
                  className="self-start text-[11px] font-bold text-neutral-500 underline decoration-dotted underline-offset-2 hover:text-[#00828F]">
            I don&apos;t have these yet
          </button>
        )
      )}

      {/* Live in-card uploading indicator (filename + progress bar + hint) */}
      {isUploading && (
        <div
          data-testid={`upload-${cfg.key}-progress`}
          className="mt-1 rounded-md border border-[#0A8B85]/30 bg-white px-3 py-2">
          <div className="flex items-center justify-between gap-2 text-[11px]">
            <span className="flex min-w-0 items-center gap-1.5 truncate font-semibold text-neutral-800">
              <FileText className="h-3 w-3 flex-shrink-0 text-[#0A8B85]"/>
              <span className="truncate">{uploadingState.filename}</span>
            </span>
            <span className="shrink-0 font-extrabold text-[#0A8B85]">
              {uploadingState.progress < 100
                ? `${uploadingState.progress}%`
                : "Processing…"}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full bg-[#0A8B85] transition-all"
              style={{ width: `${uploadingState.progress}%` }}
            />
          </div>
          {isFinancialModel && uploadingState.progress >= 99 && (
            <p className="mt-1.5 text-[10px] leading-snug text-neutral-500">
              Reading your financial model in the background. Safe to keep working.
            </p>
          )}
        </div>
      )}
      {hasFiles && (
        <ul className="mt-1 w-full space-y-1 border-t border-neutral-200 pt-2">
          {uploaded.map(d => {
            const auto = autoIds?.has?.(d.doc_id);
            return (
              <li key={d.doc_id} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="flex min-w-0 items-center gap-1 truncate text-neutral-700">
                  <FileText className="h-3 w-3 flex-shrink-0 text-[#0A8B85]"/>
                  <span className="truncate">{d.filename}</span>
                  <UploadFailedBadge doc={d} className="ml-1"
                    onRetry={() => { retryDocRef.current = d.doc_id; document.getElementById(inputId)?.click(); }} />
                  {auto && (
                    <span
                      data-testid={`auto-classified-${d.doc_id}`}
                      title="Auto-classified from filename"
                      className="ml-1 inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[#0A8B85]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#0A8B85]">
                      <Sparkles className="h-2.5 w-2.5"/> Auto
                    </span>
                  )}
                </span>
                <button
                  data-testid={`remove-doc-${d.doc_id}`}
                  onClick={() => onRemove(d.doc_id)}
                  title="Remove document"
                  className="flex-shrink-0 rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-3 w-3"/>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Trouble-uploading fallback card.
 * Zero-code catch-net for founders who can't get an upload to complete —
 * large decks blocked by ingress caps, unusual file types, slow connections.
 * Opens the user's default mail client pre-filled with:
 *   • recipient: hello@clinq.africa
 *   • subject: "Document upload help — <company or founder>"
 *   • body: their email + company + friendly context so the ops team can
 *     reply and ingest the deck manually.
 *
 * A minimal "cost-of-failure = zero" fix. Placed directly under the upload
 * tiles so a stuck founder sees it exactly when they need it.
 */
/**
 * "Having trouble uploading?" card under the upload tiles — opens the
 * support-ticket modal pre-set to "Document Upload Issue" so a stuck founder
 * can reach the Clinq team without leaving the page.
 */
function TroubleUploadingCard() {
  return (
    <div
      data-testid="trouble-uploading-card"
      className="mt-6 flex flex-col gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0">
        <p className="text-sm font-bold text-neutral-900">
          Having trouble uploading?
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-neutral-600">
          Tell our support team what happened and we&rsquo;ll help you get your
          documents into your Clinq profile.
        </p>
      </div>
      <GetHelpLink issueType="document_upload" testid="trouble-uploading-get-help"
        className="shrink-0" />
    </div>
  );
}

